import React from 'react';
import { cn } from '@/lib/utils';

type ProgressColor = 'primary' | 'secondary' | 'accent' | 'muted';

interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number;
  max?: number;
  color?: ProgressColor;
  label?: string;
  showValue?: boolean;
}

const colorMap: Record<ProgressColor, string> = {
  primary: 'bg-blue-500',
  secondary: 'bg-emerald-500',
  accent: 'bg-amber-500',
  muted: 'bg-gray-400',
};

const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  color = 'primary',
  label,
  showValue,
  className,
  ...props
}) => {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className={cn('w-full', className)} {...props}>
      {(label || showValue) && (
        <div className="flex justify-between mb-1">
          {label && <span className="text-xs text-gray-600">{label}</span>}
          {showValue && <span className="text-xs font-medium text-gray-700">{Math.round(pct)}%</span>}
        </div>
      )}
      <div className="w-full h-2 bg-gray-200 rounded-md overflow-hidden">
        <div
          role="progressbar"
          aria-valuenow={value}
          aria-valuemax={max}
          className={cn('h-full rounded-md transition-all duration-500', colorMap[color])}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export { ProgressBar };
export type { ProgressBarProps, ProgressColor };
